import React from 'react';
import { useTranslation } from 'react-i18next';
import { isCompleted } from '@/constants/matchStatus';
import { formatPoints } from '@/utils/formatPoints';
import styles from './styles.module.scss';

export interface PredictionEntry {
  user_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  predicted_score_team1: number;
  predicted_score_team2: number;
  /** Outcome the user predicted: 'team1_win' | 'draw' | 'team2_win'. */
  predicted_outcome?: string;
  /** Exact-score coefficient locked at prediction time. */
  odds_score?: number | string | null;
  /**
   * Live outcome coefficients (team1_win / draw / team2_win). Backend
   * attaches these on the per-match predictions endpoint for non-finished
   * matches; used together with `predicted_outcome` to surface the user's
   * potential outcome coefficient.
   */
  coefficients?: {
    team1_win?: number | string | null;
    draw?: number | string | null;
    team2_win?: number | string | null;
  };
  total_points?: number;
  points_breakdown?: {
    score: number;
    result: number;
    total: number;
  };
}

export interface MatchInfo {
  team1: string;
  team2: string;
  team1_short?: string;
  team2_short?: string;
  team1_logo_url?: string;
  team2_logo_url?: string;
  score_team1?: number | null;
  score_team2?: number | null;
  status: string;
  match_date?: string;
}

export interface PredictionsTableProps {
  title: string;
  subtitle: string;
  logoUrl?: string;
  logoAlt?: string;
  match: MatchInfo;
  predictions: PredictionEntry[];
  emptyMessage?: string;
  className?: string;
  /** When true, skip the outer card wrapper (use inside MainSection). */
  noWrapper?: boolean;
}

const getInitial = (name: string) => (name || '?').trim().charAt(0).toUpperCase();

const formatOdds = (value: number) => value.toFixed(2);

// Aligns with the canonical project rule (`CoefficientsCard.isDefaultCoefficient`,
// `MatchDetailCard.isCoeffEmpty`, `formatCoeff` in MatchPreviewCard): a value
// of `1.00` is the placeholder for "no real coefficient", not a real odd —
// real coefficients always carry a margin > 1.
const coerceCoeff = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  return Number.isFinite(n) && n > 1 ? n : null;
};

// DB stores `predicted_outcome` as one of `team1_win | draw | team2_win`.
// `home_win` / `away_win` are computed in-memory by `PointsCalculator`
// but never persisted, so no defensive branches needed.
const pickOutcomeCoeff = (
  prediction: PredictionEntry,
): number | null => {
  const c = prediction.coefficients;
  if (!c || !prediction.predicted_outcome) return null;
  switch (prediction.predicted_outcome) {
    case 'team1_win':
      return coerceCoeff(c.team1_win);
    case 'team2_win':
      return coerceCoeff(c.team2_win);
    case 'draw':
      return coerceCoeff(c.draw);
    default:
      return null;
  }
};

const formatKickoff = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

export const PredictionsTable: React.FC<PredictionsTableProps> = ({
  title,
  subtitle,
  logoUrl,
  logoAlt,
  match,
  predictions,
  emptyMessage,
  className,
  noWrapper,
}) => {
  const { t } = useTranslation('tournament');
  const isFinished = isCompleted(match.status);
  const hasFinalScore =
    isFinished &&
    match.score_team1 !== null && match.score_team1 !== undefined &&
    match.score_team2 !== null && match.score_team2 !== undefined;

  // Scheduled / live (no final score) → coefficient column. Finished → points.
  const showPoints = isFinished;
  const columnLabel = showPoints ? t('points') : t('odds');
  const kickoffTime = formatKickoff(match.match_date);

  const matchSubheader = (
    <div className={styles.matchSubheader}>
      <div className={styles.mcMain}>
        <div className={`${styles.mcTeam} ${styles.mcTeam1}`}>
          <h3 className={styles.mcName}>{match.team1}</h3>
          {match.team1_logo_url ? (
            <img
              src={match.team1_logo_url}
              alt={match.team1}
              className={styles.mcLogo}
            />
          ) : (
            <span className={styles.mcLogoPlaceholder} aria-hidden="true" />
          )}
        </div>
        <div className={styles.mcCenter}>
          {hasFinalScore ? (
            <div className={styles.mcScore}>
              <span>{match.score_team1}</span>
              <span className={styles.mcScoreSep}>:</span>
              <span>{match.score_team2}</span>
            </div>
          ) : (
            <div className={styles.mcKickoff}>{kickoffTime || '—'}</div>
          )}
        </div>
        <div className={`${styles.mcTeam} ${styles.mcTeam2}`}>
          {match.team2_logo_url ? (
            <img
              src={match.team2_logo_url}
              alt={match.team2}
              className={styles.mcLogo}
            />
          ) : (
            <span className={styles.mcLogoPlaceholder} aria-hidden="true" />
          )}
          <h3 className={styles.mcName}>{match.team2}</h3>
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      {matchSubheader}

      <div className={styles.tableHeader}>
        <span className={styles.colName}>{t('participant')}</span>
        <span className={styles.colScore}>{t('score')}</span>
        <span className={styles.colPoints}>{columnLabel}</span>
      </div>

      <div className={styles.tableBody}>
        {predictions.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>
              {emptyMessage || t('noPredictionsYet')}
            </p>
          </div>
        ) : (
          predictions.map((prediction, index) => {
            const position = index + 1;

            const fullName = `${prediction.first_name || ''} ${prediction.last_name || ''}`.trim();
            const displayName = fullName ||
              prediction.username ||
              `User ${prediction.user_id}`;

            // Post-finish: keep the project-canonical "+X pts" cell that
            // existed before BP-81 — icon + total. The points-breakdown
            // (`+score +result =total`) lives in the per-user prediction
            // detail surface, not in this comparison list.
            // Pre-finish: show both potential-points coefficients stacked
            // vertically (`Точн ×8.50` / `Исх ×1.80`) so participants see
            // the upside of their predicted outcome alongside their
            // exact-score upside before the match finishes.
            const totalPoints = Number(prediction.total_points) || 0;
            const hasPoints = showPoints && totalPoints > 0;

            const scoreCoeff = !showPoints ? coerceCoeff(prediction.odds_score) : null;
            const outcomeCoeff = !showPoints ? pickOutcomeCoeff(prediction) : null;
            const hasAnyCoefficient = scoreCoeff !== null || outcomeCoeff !== null;

            const pointsCellClass = `${styles.pointsCell} ${(hasPoints || hasAnyCoefficient) ? '' : styles.zero}`;

            const potentialAriaLabel = !showPoints && hasAnyCoefficient
              ? t('potentialPointsAria', {
                  score: scoreCoeff !== null ? formatOdds(scoreCoeff) : '—',
                  outcome: outcomeCoeff !== null ? formatOdds(outcomeCoeff) : '—',
                })
              : undefined;

            return (
              <div
                key={prediction.user_id}
                className={styles.tableRow}
                data-position={position}
              >
                <span className={styles.participantCluster}>
                  <span className={styles.avatar} aria-hidden="true">
                    {getInitial(displayName)}
                  </span>
                  <span className={styles.participantName}>{displayName}</span>
                </span>

                <span className={styles.scoreCell}>
                  <span>{prediction.predicted_score_team1}</span>
                  <span className={styles.scoreSeparator}>:</span>
                  <span>{prediction.predicted_score_team2}</span>
                </span>

                <span className={pointsCellClass}>
                  {showPoints ? (
                    <>
                      {hasPoints && (
                        <i className={`icon-chevrons-up ${styles.pointsIcon}`} aria-hidden="true" />
                      )}
                      <span className={styles.pointsLabel}>
                        {hasPoints ? `+${formatPoints(totalPoints)} pts` : '0 pts'}
                      </span>
                    </>
                  ) : hasAnyCoefficient ? (
                    <span
                      className={styles.potentialCoefficients}
                      aria-label={potentialAriaLabel}
                    >
                      <span className={styles.coeffPair} aria-hidden="true">
                        <span className={styles.coeffPrefix}>{t('coeffPrefixScore')}</span>
                        <span className={styles.coeffValue}>
                          {scoreCoeff !== null ? formatOdds(scoreCoeff) : '—'}
                        </span>
                      </span>
                      <span className={styles.coeffPair} aria-hidden="true">
                        <span className={styles.coeffPrefix}>{t('coeffPrefixResult')}</span>
                        <span className={styles.coeffValue}>
                          {outcomeCoeff !== null ? formatOdds(outcomeCoeff) : '—'}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className={styles.pointsLabel}>—</span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  if (noWrapper) return <div className={styles.card}>{content}</div>;

  return (
    <div className={`${styles.main} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          {logoUrl && (
            <img src={logoUrl} alt={logoAlt || ''} className={styles.logo} />
          )}
          <div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>
        {predictions.length > 0 && (
          <span className={styles.countBadge}>{predictions.length}</span>
        )}
      </div>
      {content}
    </div>
  );
};

export default PredictionsTable;
